'use client';import { ContactUsWidgetTypeEnum } from '@parkingbase/ecommerce-api-test';


import { useEffect, useRef, useCallback } from 'react';
import { usePathname } from '@/lib/app-router';
import { useContactUsStore } from '@/stores/contact-us.store';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/user.store';
import { UserService } from '@/services/user.service';

import { logger } from '@/lib/logger';
import { loadCobrowse } from '@/lib/cobrowse/loadCobrowse';
import { resolveEffectiveSupportWidget } from '@/lib/support-widget-utils';
/// <reference types="@/types/support-widget" />

const FRESHDESK_SCRIPT_ID = 'freshworks-widget-script';
const ZENDESK_SCRIPT_ID = 'ze-snippet';
const FRESHDESK_FALLBACK_KEY = process.env.NEXT_PUBLIC_FRESHDESK_KEY;

function removeScript(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * Sign-in / auth screens must not show the support ("Rechat") chat widget
 * (PB-2609 / PB-2631). Matches `/login` and any `/auth` segment, including
 * company-prefixed paths like `/c/{companyId}/login`.
 */
function isSupportWidgetSuppressed(pathname: string): boolean {
  return /\/(login|auth)(\/|$)/.test(pathname);
}

export function SupportWidgetProvider({ children }: { children: React.ReactNode }) {
  const { contactUs, fetchContactUs } = useContactUsStore();
  const theme = useThemeStore((s) => s.theme);
  const { token, user } = useAuthStore();
  const scriptInjectedRef = useRef(false);
  const pathname = usePathname();
  const widgetSuppressed = isSupportWidgetSuppressed(pathname);

  /* Effective widget — falls back to FreshDesk env key when ContactUs config has no widgetId,
     matching legacy vm-reservation-website AppHeader behaviour. */
  const { type: effectiveWidgetType, id: effectiveWidgetId } = resolveEffectiveSupportWidget(
    contactUs,
    FRESHDESK_FALLBACK_KEY
  );

  /* Fetch contactUs config on mount */
  useEffect(() => {
    fetchContactUs();
  }, [fetchContactUs]);

  const injectFreshdesk = useCallback((widgetId: string) => {
    if (document.getElementById(FRESHDESK_SCRIPT_ID)) return;

    window.fwSettings = { widget_id: parseInt(widgetId, 10) };

    const script = document.createElement('script');
    script.id = FRESHDESK_SCRIPT_ID;
    script.src = `https://widget.freshworks.com/widgets/${widgetId}.js`;
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const injectZendesk = useCallback(
    (widgetId: string) => {
      if (document.getElementById(ZENDESK_SCRIPT_ID)) return;

      window.zESettings = {
        webWidget: {
          color: { theme: theme?.colorPrimary },
          contactForm: { attachments: true },
        },
      };

      const script = document.createElement('script');
      script.id = ZENDESK_SCRIPT_ID;
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${widgetId}`;
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    },
    [theme?.colorPrimary]
  );

  /* Inject widget script when an effective widget config is available.
     Skipped on sign-in/auth routes; if we navigate onto one, the cleanup from
     the previous run removes any already-injected widget (PB-2609 / PB-2631). */
  useEffect(() => {
    if (widgetSuppressed) return;
    if (!effectiveWidgetId || !effectiveWidgetType || scriptInjectedRef.current) return;
    if (typeof window === 'undefined') return;

    try {
      if (effectiveWidgetType === ContactUsWidgetTypeEnum.FRESHDESK) {
        injectFreshdesk(effectiveWidgetId);
      } else if (effectiveWidgetType === ContactUsWidgetTypeEnum.ZENDESK) {
        injectZendesk(effectiveWidgetId);
      }
      scriptInjectedRef.current = true;
    } catch (error) {
      logger.error('Failed to inject support widget script', error, {
        feature: 'support-widget',
        route: 'inject',
      });
    }

    return () => {
      removeScript(FRESHDESK_SCRIPT_ID);
      removeScript(ZENDESK_SCRIPT_ID);
      if (typeof window !== 'undefined') {
        delete window.fwSettings;
        delete window.zESettings;
      }
      scriptInjectedRef.current = false;
    };
  }, [effectiveWidgetId, effectiveWidgetType, injectFreshdesk, injectZendesk, widgetSuppressed]);

  /* Authenticate Zendesk messenger with user JWT */
  useEffect(() => {
    if (!token || !effectiveWidgetId || effectiveWidgetType !== ContactUsWidgetTypeEnum.ZENDESK) {
      return;
    }
    if (typeof window === 'undefined' || typeof window.zE !== 'function') return;

    const authenticateZendesk = async () => {
      try {
        const userService = new UserService();
        const tokenData = await userService.getZendeskToken();
        const accessToken = (tokenData as unknown as Record<string, string>).access_token;
        if (accessToken && typeof window.zE === 'function') {
          window.zE('messenger', 'loginUser', (callback: (jwt: string) => void) => {
            callback(accessToken);
          });
        }
      } catch (error) {
        logger.error('Failed to authenticate Zendesk', error, {
          feature: 'support-widget',
          route: 'zendesk-auth',
        });
      }
    };

    authenticateZendesk();
  }, [token, effectiveWidgetId, effectiveWidgetType]);

  /* Identify user in Freshdesk */
  useEffect(() => {
    if (!user || !effectiveWidgetId || effectiveWidgetType !== ContactUsWidgetTypeEnum.FRESHDESK) {
      return;
    }
    if (typeof window === 'undefined' || typeof window.FreshworksWidget !== 'function') return;

    const name = `${user.contactInfo?.firstName ?? ''} ${user.contactInfo?.lastName ?? ''}`.trim();
    const email = user.emailUserId || '';
    if (email) {
      window.FreshworksWidget('identify', 'ticketForm', { email, name });
    }
  }, [user, effectiveWidgetId, effectiveWidgetType]);

  /**
   * Cobrowse — co-browsing support tool. Independent of Freshdesk/Zendesk:
   * runs whenever `contactUs.cobrowserLicenseKey` is configured AND we have
   * a logged-in user (the SDK's `customData` carries user identity for the
   * support agent). Singleton loader (`@/lib/cobrowse/loadCobrowse`) handles
   * the script-tag injection idempotently; the per-render ref below prevents
   * redundant `.start()` calls within a stable (license, user) tuple.
   *
   * ## Invariants:
   * - License key MUST come from the ContactUs API (never env / hardcoded).
   *   Why: each tenant ships its own cobrowse account; mixing keys leaks
   *   support sessions across tenants.
   * - `customData` MUST include `user_id` so the cobrowse agent can match
   *   the session back to the user in our system.
   * - The cleanup MUST reset `cobrowseStartedRef` to false. Without this,
   *   a user switch (sign out → sign in as different user) would skip
   *   re-setting customData and the new user's session would be tagged
   *   with the previous user's identity.
   */
  const cobrowseStartedRef = useRef(false);
  useEffect(() => {
    const licenseKey = contactUs?.cobrowserLicenseKey;
    if (!licenseKey || !user || cobrowseStartedRef.current) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;

    loadCobrowse().then((sdkUnknown) => {
      if (cancelled) return;
      if (!sdkUnknown) {
        logger.warn('Cobrowse script failed to load', {
          feature: 'support-widget',
          route: 'cobrowse-load',
        });
        return;
      }

      const sdk = sdkUnknown as {
        license: string;
        customData: Record<string, string>;
        client: () => Promise<void>;
        start?: () => void;
      };

      sdk.license = licenseKey;
      sdk.customData = {
        user_email: user.emailUserId ?? '',
        user_id: user.userId ?? '',
        user_name:
          `${user.contactInfo?.firstName ?? ''} ${user.contactInfo?.lastName ?? ''}`.trim(),
      };

      sdk
        .client()
        .then(() => {
          if (cancelled) return;
          if (typeof sdk.start !== 'function') {
            logger.warn('Cobrowse loaded but .start is missing', {
              feature: 'support-widget',
              route: 'cobrowse-init',
            });
            return;
          }
          try {
            sdk.start();
            cobrowseStartedRef.current = true;
          } catch (error) {
            logger.error('Cobrowse start failed', error, {
              feature: 'support-widget',
              route: 'cobrowse-start',
            });
          }
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          logger.error('Cobrowse client() failed', error, {
            feature: 'support-widget',
            route: 'cobrowse-client',
          });
        });
    });

    return () => {
      cancelled = true;
      cobrowseStartedRef.current = false;
      // Stop the active session so `customData` / `license` can't leak across
      // user or tenant switches (see the cross-tenant invariant in the
      // docblock above). The next effect will set fresh customData and call
      // start() again. `stop()` is guarded because older SDK builds may not
      // expose it and a runtime throw here would mask other cleanup work.
      try {
        const cb = (typeof window !== 'undefined' ? window.CobrowseIO : undefined) as
          | { stop?: () => void }
          | undefined;
        if (typeof cb?.stop === 'function') {
          cb.stop();
        }
      } catch (error) {
        logger.warn('Cobrowse stop failed during cleanup', {
          feature: 'support-widget',
          route: 'cobrowse-stop',
        });
      }
    };
  }, [contactUs?.cobrowserLicenseKey, user]);

  return <>{children}</>;
}
